import type { AddressRetrieveFormFields } from '../../../core/models/address-retrieve';
import type { SuggestedAddress } from '../../../core/models/address-validate';

/** Current form values preserved when AVS does not supply a replacement. */
export interface SuggestedAddressFormContext {
  companyOrFullName?: string;
  suiteAptUnit: string;
  /**
   * Residential flag from Canada Post autocomplete retrieve `type`.
   * AVS suggestions omit residential, so the form value is carried forward.
   */
  residential: boolean;
}

/**
 * Resolves addressee for form patching, keeping the user's company when AVS omits it.
 */
function resolveSuggestedAddressee(
  suggestedAddressee: string,
  currentCompanyOrFullName: string,
): string {
  const trimmedSuggested = suggestedAddressee.trim();
  return trimmedSuggested.length > 0 ? trimmedSuggested : currentCompanyOrFullName.trim();
}

/**
 * Resolves suite/apt/unit, keeping the user's entry when AVS does not suggest line 2.
 * Mirrors ESN only applying alternateAddress.addressLine2 when present.
 */
function resolveSuggestedSuite(suggestedAddressLine2: string, currentSuiteAptUnit: string): string {
  const trimmedSuggested = suggestedAddressLine2.trim();
  return trimmedSuggested.length > 0 ? trimmedSuggested : currentSuiteAptUnit.trim();
}

/**
 * Maps AVS suggested address fields to Ship From / Ship To form values while preserving
 * company, suite, and residential when AVS did not supply replacements.
 *
 * Preserves `residential` from the form: AVS does not return it, and the value comes only
 * from Canada Post autocomplete retrieve `type`.
 */
export function mapSuggestedAddressToAddressSectionFields(
  suggestedAddress: SuggestedAddress,
  currentValue: SuggestedAddressFormContext,
): AddressRetrieveFormFields & { companyOrFullName: string } {
  const province = suggestedAddress.provinceCode.trim();

  return {
    companyOrFullName: resolveSuggestedAddressee(
      suggestedAddress.addressee,
      currentValue.companyOrFullName ?? '',
    ),
    streetAddress: suggestedAddress.addressLine1.trim(),
    suiteAptUnit: resolveSuggestedSuite(suggestedAddress.addressLine2, currentValue.suiteAptUnit),
    city: suggestedAddress.municipalityName.trim(),
    province: province.length > 0 ? province : null,
    postalCode: suggestedAddress.postalCode.trim(),
    residential: currentValue.residential === true,
  };
}

/**
 * Maps AVS suggested address fields to return address form values while preserving suite
 * and residential when AVS did not supply replacements.
 */
export function mapSuggestedAddressToReturnAddressFields(
  suggestedAddress: SuggestedAddress,
  currentValue: Pick<SuggestedAddressFormContext, 'suiteAptUnit' | 'residential'>,
): AddressRetrieveFormFields {
  const province = suggestedAddress.provinceCode.trim();

  return {
    streetAddress: suggestedAddress.addressLine1.trim(),
    suiteAptUnit: resolveSuggestedSuite(suggestedAddress.addressLine2, currentValue.suiteAptUnit),
    city: suggestedAddress.municipalityName.trim(),
    province: province.length > 0 ? province : null,
    postalCode: suggestedAddress.postalCode.trim(),
    residential: currentValue.residential === true,
  };
}
